import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormControl, Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useGrid, useModal, useToast, useAuth, usePage, usePopup } from "@/comn/hooks"; // hook
import {
    URLS,
    APIS,
    SF_BLL_CNCL_DTL_SRCH,
    SF_BLL_CNCL_DTL,
    SG_BLL_CNCL_DTL_BLL_LIST,
    SG_BLL_CNCL_DTL_ITM_LIST,
    SG_BLL_CNCL_DTL_LIST,
    SF_CNCL,
} from "./services/OsciBllCnclService"; // 서비스
import { APIS_COMN, SF_COMN_TANSAD_INFO } from "@/tz/go/egaz/osci/colt/comn/OsciColtComnService"; // 공통 서비스
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn";

/*
 * Bill Cancellation Registration
 * !== 고지 취소 등록 ==!
 */
export const OSCI0403003S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0056" },
            { area: "comnCd", comnCd: "OSC0057" },
            { area: "comnCd", comnCd: "OSC0058" },
            { area: "comnCd", comnCd: "OSC0067" },
            { area: "comnCd", comnCd: "OSC0072" },
            { area: "comnCd", comnCd: "OSC0018" },
            { area: "comnCd", comnCd: "OSC0020" },
            { area: "comnCd", comnCd: "OSC0023" },
        ],
    });

    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0021"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const popup = usePopup(); // Popup Hook !== Popup Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { bllMgmtId } = useParams();
    const { mode } = useParams();
    const [searchParams] = useSearchParams();
    const cnclRqstSn = searchParams.get("cnclRqstSn") || undefined;

    // 폼 처리
    const [isVisibleBll, setIsVisibleBll] = useState(false);
    const [isVisibleTansad, setIsVisibleTansad] = useState(false);
    const [isVisibleCancellation, setIsVisibleCancellation] = useState(false);

    // 항목 처리
    const [isBllNoSrch, setIsBllNoSrch] = useState(true);
    const [isTansadNoSrch, setIsTansadNoSrch] = useState(false);

    // grid 그리기
    const [vsdDtls, setVsdDtls] = useState(false);
    const [vsdItms, setVsdItms] = useState(false);

    const [bllCnclDtlBllList, setBllCnclDtlBllList] = useState(comnUtils.getGridData([]));
    const [bllCnclDtlItmList, setBllCnclDtlItmList] = useState(comnUtils.getGridData([]));

    // 버튼 처리
    const [isVisibleSaveBtn, setIsVisibleSaveBtn] = useState(false);

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Bill Cancellation Detail Search !== 고지 취소 상세 조회 ==!
        bllCnclDtlSrch: useForm({
            defaultSchema: SF_BLL_CNCL_DTL_SRCH,
            defaultValues: {
                bllMgmtId: bllMgmtId,
                reffKeyNo1: auth.get("cstmCd"),
            },
        }),
        // Bill Cancellation Detail  !== 고지 취소 상세 폼 ==!
        bllCnclDtl: useForm({
            defaultSchema: SF_BLL_CNCL_DTL,
        }),
        // Common TANSAD Info  !== 공통 TANSAD정보 폼 ==!
        comnTansadInfo: useForm({
            defaultSchema: SF_COMN_TANSAD_INFO,
        }),
        // Cancellation  !== 취소 등록 폼 ==!
        cnclForm: useForm({
            defaultSchema: SF_CNCL,
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Bill Cancellation Detail Bill List !== 고지 취소 상세화면의 고지목록 ==!
        bllCnclDtlBllList: useGrid({
            defaultSchema: SG_BLL_CNCL_DTL_LIST,
        }),
        bllCnclDtlItmList: useGrid({
            defaultSchema: SG_BLL_CNCL_DTL_ITM_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Bill Cancellation Detail !== 고지 취소 상세 조회 ==!
        getBllCnclDtl: useFetch({
            api: (bllNo) => APIS.getBllCnclDtl({ ...form.bllCnclDtlSrch.getValues(), cnclRqstSn }),
            onSuccess: (data) => {
                const bllCnclData = data.bllCnclDtl.content;
                if (!bllCnclData?.bllNo) {
                    modal.openModal({
                        content: "msg.osc.00182", // Please check Bill No.
                    });
                    return;
                }
                //통합 고지서인 경우
                if (bllCnclData?.bllTpCd == "G") {
                    modal.openModal({
                        content: "msg.osc.00311", // Combined bills cannot be cancelled.
                    });
                    setIsVisibleBll(false);
                    setIsVisibleCancellation(false);
                    return;
                }

                // 컨트롤번호가 없는 경우
                if (!bllCnclData?.ctrlNo) {
                    modal.openModal({
                        content: "msg.osc.00268", // This bill cannot be processed because it does not have a Control Number.
                    });
                    setIsVisibleBll(false);
                    setIsVisibleCancellation(false);
                    return;
                }

                // 취소 된 고지인 경우
                if (bllCnclData?.dspoYn == "Y") {
                    modal.openModal({
                        content: "msg.osc.00167", // Notice has already been cancelled.
                    });
                    setIsVisibleBll(false);
                    setIsVisibleTansad(false);
                    setIsVisibleCancellation(false);
                    return;
                }

                // 납부 기한이 지난 경우
                const today = new Date();
                const todayStr =
                    today.getFullYear().toString() +
                    String(today.getMonth() + 1).padStart(2, "0") +
                    String(today.getDate()).padStart(2, "0");
                const isExpired = !!bllCnclData?.payDdlnDt && bllCnclData.payDdlnDt < todayStr;
                if (isExpired) {
                    modal.openModal({
                        content: "msg.osc.00310", // The bill's payment due date has already passed, so the bill cannot be cancelled.
                    });
                    setIsVisibleBll(false);
                    setIsVisibleTansad(false);
                    setIsVisibleCancellation(false);
                    return;
                }

                // 이미 납부된 경우
                if (bllCnclData?.bllStatCd == "C") {
                    modal.openModal({
                        content: "msg.osc.00168", // Payment received. Notice cannot be cancelled.
                    });
                    setIsVisibleBll(false);
                    setIsVisibleTansad(false);
                    setIsVisibleCancellation(false);
                    return;
                }

                // form 데이터 세팅
                form.bllCnclDtl.setValues({
                    ...bllCnclData,
                });

                if (bllCnclData.bllDtls && bllCnclData.bllDtls.length > 0) {
                    setVsdDtls(true);
                    setBllCnclDtlBllList(comnUtils.getGridData(bllCnclData.bllDtls));
                }

                if (bllCnclData.bllItms && bllCnclData.bllItms.length > 0) {
                    setVsdItms(true);
                    setBllCnclDtlItmList(comnUtils.getGridData(bllCnclData.bllItms));
                }

                setIsVisibleBll(true);
                setIsVisibleTansad(false);
                setIsVisibleCancellation(true);

                // Taxpayer TIN
                let pyerMgmtNoNm = "";
                if (bllCnclData?.pyerMgmtNo == null || bllCnclData?.pyerNm == null) {
                    if (bllCnclData?.pyerMgmtNo != null) {
                        pyerMgmtNoNm = bllCnclData?.pyerMgmtNo;
                    } else if (bllCnclData?.pyerNm != null) {
                        pyerMgmtNoNm = bllCnclData?.pyerNm;
                    }
                } else {
                    pyerMgmtNoNm = bllCnclData?.pyerMgmtNo + ", " + bllCnclData?.pyerNm;
                }
                form.bllCnclDtl.setValue("pyerMgmtNoNm", pyerMgmtNoNm);

                // Reference Key
                if (bllCnclData.reffKeyTpCd) {
                    let reffKeyNo =
                        bllCnclData?.reffKeyNo1 +
                        "-" +
                        bllCnclData?.reffKeyNo2 +
                        (bllCnclData?.reffKeyNo3 ? "-" + bllCnclData?.reffKeyNo3 : "") +
                        (bllCnclData?.reffKeyNo4 ? "-" + bllCnclData?.reffKeyNo4 : "");
                    form.bllCnclDtl.setValue("reffKey", reffKeyNo);
                }

                // Register
                form.bllCnclDtl.setValue("bllRgsrIdNm", bllCnclData?.bllRgsrId + ", " + bllCnclData?.bllRgsrNm);

                if (cnclRqstSn) {
                    form.cnclForm.setValue("dspoCdSave", bllCnclData?.dspoCd);
                    form.cnclForm.setValue("dspoRmrkSave", bllCnclData?.dspoRmrk);
                } else if (bllCnclData.cnclRqstSn && bllCnclData.cnclStatCd == "R") {
                    form.cnclForm.setValue("dspoCdSave", bllCnclData?.dspoCd);
                    form.cnclForm.setValue("dspoRmrkSave", bllCnclData?.dspoRmrk);
                } else {
                    form.cnclForm.setValue("dspoCdSave", "");
                    form.cnclForm.setValue("dspoRmrkSave", "");
                }
                form.cnclForm.setEditable(true);
                setIsVisibleSaveBtn(true);

                form.bllCnclDtlSrch.setValues({
                    ...data.bllCnclDtl.content,
                });
            },
            onError: () => {
                modal.openModal({
                    content: "msg.osc.00182", // Please check Bill No.
                });
            },
            showToast: true,
        }),
        // Save Bill Cancellation !== 고지서 취소 저장 ==!
        saveBllCncl: useFetch({
            api: (dtlData, cnclData, mode) => APIS.saveBllCncl(dtlData, cnclData, mode),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006", // 성공적으로 처리 되었습니다.
                    onClose: () => {
                        let bllTpCd = form.bllCnclDtlSrch.getValue("bllTpCd");
                        let bllSysmCd = form.bllCnclDtlSrch.getValue("bllSysmCd");
                        let bllYy = form.bllCnclDtlSrch.getValue("bllYy");
                        let bllSrno = form.bllCnclDtlSrch.getValue("bllSrno");
                        let bllDgr = form.bllCnclDtlSrch.getValue("bllDgr");
                        navigate(`${URLS.osci0403002q}/${bllTpCd}${bllSysmCd}${bllYy}${bllSrno}${bllDgr}`);
                        // mode == "INSERT" ? navigate(`${URLS.osci0403001q}`) : navigate(`${URLS.osci0403002q}/${bllNo}`);
                    },
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    console.log(error.response.data);
                }
                modal.openModal({
                    content: "msg.osc.00015", // 처리중 문제가 발생하였습니다.
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get Bill Cancellation Registration Detail !== 고지 취소 등록 상세 조회 ==!
        getBllCnclRgsrDtl: () => {
            form.bllCnclDtlSrch.handleSubmit(
                () => {
                    let bllNo = form.bllCnclDtlSrch.getValue("bllNo");
                    let bllMgmtIdSrch = form.bllCnclDtlSrch.getValue("bllMgmtId");
                    let bllTpCd = form.bllCnclDtlSrch.getValue("bllTpCd");
                    let bllSysmCd = form.bllCnclDtlSrch.getValue("bllSysmCd");
                    let bllYy = form.bllCnclDtlSrch.getValue("bllYy");
                    let bllSrno = form.bllCnclDtlSrch.getValue("bllSrno");
                    let bllDgr = form.bllCnclDtlSrch.getValue("bllDgr");
                    if (
                        !!!bllNo &&
                        !!!bllMgmtIdSrch &&
                        (!!!bllTpCd || !!!bllSysmCd || !!!bllYy || !!!bllSrno || !!!bllDgr)
                    ) {
                        modal.openModal({
                            content: "msg.osc.00182", // Please check Bill No.
                        });
                        setIsVisibleBll(false);
                        setIsVisibleTansad(false);
                        setIsVisibleCancellation(false);
                        return false;
                    }
                    fetch.getBllCnclDtl.fetch();
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // 데이터가 유효하지 않습니다.
                },
            )();
        },
        // Get Processing History List !== 처리 이력 목록 조회 ==!
        getPrcsHistLst: () => {
            let tansadNo = form.comnTansadInfo.getValue("tansadNo");
            tansadNo = colUtils.setMask(tansadNo, "tansadNo");

            // Declaration 처리이력 (팝업)
            popup.openPopup({
                id: "tansadPrcsHistLstPpup",
                url: URLS.clri0801029q,
                params: tansadNo,
                size: "lg",
                callback: (data: any) => {
                    console.log(data);
                },
            });
        },
        // Save Bill Cancellation !== 고지 취소 저장 ==!
        saveBllCncl: form.cnclForm.handleSubmit(
            () => {
                const dtlData = { ...form.bllCnclDtlSrch.getValues() };

                var saveCnclRqstSn = cnclRqstSn;

                var save_mode = mode;
                if (dtlData.cnclStatCd == "R") {
                    saveCnclRqstSn = dtlData.cnclRqstSn;
                    save_mode = "UPDATE";
                }

                const cnclData = { ...form.cnclForm.getValues(), cnclRqstSn: saveCnclRqstSn };

                modal.openModal({
                    content: "msg.osc.00090", // 저장 하시겠습니까 ?
                    onConfirm: () => {
                        fetch.saveBllCncl.fetch(dtlData, cnclData, save_mode);
                    },
                });
            },
            () => {
                toast.showToast({ type: "warning", content: "msg.osc.00003" });
            },
        ),
    };

    const render = {
        grid_bllCnclDtlBllList: {
            cell: {
                bllNo: (props: any) => {
                    const { rowValues } = props;
                    return <Link to={`${URLS.osci0403003s}/${rowValues.bllMgmtId}`}>{`${rowValues.bllNo}`}</Link>;
                },
            },
        },
        grid_bllCnclDtlItmList: {
            head: {},
            cell: {},
            edit: {},
        },
    };

    const setSrchTp = (srchTp: any) => {
        if (srchTp == "B") {
            setIsBllNoSrch(true);
            setIsTansadNoSrch(false);
        } else if (srchTp == "T") {
            setIsBllNoSrch(false);
            setIsTansadNoSrch(true);
        }
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        if (mode == "UPDATE") {
            handler.getBllCnclRgsrDtl();
            form.bllCnclDtlSrch.setEditable(false);
        }
        form.comnTansadInfo.setEditable(false);
        form.bllCnclDtl.setEditable(false);
    }, []);

    return (
        <Page>
            {/* 고지 취소 상세 조회 */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.bllCnclDtlSrch.schema.bllNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Label {...form.bllCnclDtlSrch.schema.bllMgmtId}></Group.Label>
                            <Group.Col>
                                {/* 고지번호 */}
                                <Group.Control
                                    {...form.bllCnclDtlSrch.schema.bllTpCd}
                                    onPaste={(e: any) => {
                                        e.preventDefault();
                                        const data = e.clipboardData.getData("Text").replaceAll("-", "");

                                        form.bllCnclDtlSrch.setValue("bllTpCd", data.substring(0, 1));
                                        form.bllCnclDtlSrch.setValue("bllSysmCd", data.substring(1, 5));
                                        form.bllCnclDtlSrch.setValue("bllYy", data.substring(5, 7));
                                        form.bllCnclDtlSrch.setValue("bllSrno", data.substring(7, 14));
                                        form.bllCnclDtlSrch.setValue("bllDgr", data.substring(14, 15));
                                    }}
                                />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllCnclDtlSrch.schema.bllSysmCd} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllCnclDtlSrch.schema.bllYy} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllCnclDtlSrch.schema.bllSrno} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllCnclDtlSrch.schema.bllDgr} />
                            </Group.Col>
                        </Group.Row>
                    </Group.Section>
                    {mode == "INSERT" && (
                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.bllCnclDtlSrch.reset();
                                        setIsBllNoSrch(true);
                                        setIsVisibleBll(true);
                                        setIsTansadNoSrch(false);
                                        setIsVisibleTansad(false);
                                        form.bllCnclDtl.reset();
                                        form.comnTansadInfo.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="search"
                                    onClick={() => {
                                        handler.getBllCnclRgsrDtl();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    )}
                </Group.Body>
            </Group>
            {/* 고지 상세내역 */}
            {isVisibleBll && (
                <>
                    {/* Bill */}
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_BLL_DTL"} titleSize={2}></Group.Title>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.bllNo} />
                                    <Group.Control {...form.bllCnclDtl.schema.bllDt} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.ctrlNo} />
                                    <Group.Control {...form.bllCnclDtl.schema.zmpDt} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.bllTpCdSec} />
                                    <Group.Control {...form.bllCnclDtl.schema.bllStatCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.pyerMgmtNo} />
                                    <Group.Control {...form.bllCnclDtl.schema.payDdlnDt} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.reffKeyTpCd} />
                                    <Group.Control {...form.bllCnclDtl.schema.reffKey} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.feeAmt} />
                                    <Group.Control {...form.bllCnclDtl.schema.bllRgsrId} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.lastDdlnXtnsDt} />
                                    <Group.Control {...form.bllCnclDtl.schema.xtnsDd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.lastDdlnXtnsRsn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.dspoCd} />
                                    <Group.Control {...form.bllCnclDtl.schema.dspoDt} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.bllCnclDtl.schema.dspoRmrk} />
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                    {vsdDtls && (
                        <Group>
                            <Group.Body>
                                {" "}
                                <Layout direction="row">
                                    <Layout.Left>
                                        <Group.Title title={"T_BLL_DTL_1"} titleSize={2}></Group.Title>
                                    </Layout.Left>
                                </Layout>
                                <Grid
                                    {...grid.bllCnclDtlBllList.grid}
                                    data={bllCnclDtlBllList}
                                    render={render.grid_bllCnclDtlBllList}
                                />
                            </Group.Body>
                        </Group>
                    )}
                    {vsdItms && (
                        <Group>
                            <Group.Body>
                                <Group.Title title={"T_ITM_LST"} titleSize={2}></Group.Title>
                                <Group.Section>
                                    <Grid
                                        {...grid.bllCnclDtlItmList.grid}
                                        data={bllCnclDtlItmList}
                                        render={render.grid_bllCnclDtlItmList}
                                    />
                                </Group.Section>
                            </Group.Body>
                        </Group>
                    )}
                </>
            )}
            {/* TANSAD 상세내역 */}
            {isVisibleTansad && (
                <>
                    {/* TANSAD */}
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_TANSAD_GNRL"} titleSize={2}></Group.Title>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.tansadNo} />
                                    <Group.Control {...form.comnTansadInfo.schema.tansadDt} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.regm} />
                                    <Group.Control {...form.comnTansadInfo.schema.csclPlnCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.exppnImppnTinNm} />
                                    <Group.Control {...form.comnTansadInfo.schema.dcltTinNm} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.dclrPrcssSttsCd} controlSize={"1"} />
                                    <Layout valign="center">
                                        <Button
                                            onClick={() => {
                                                handler.getPrcsHistLst();
                                            }}
                                        >
                                            {t("B_PRCSS_HIST")}
                                        </Button>
                                    </Layout>
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                    {/* Bill List */}
                    <Group>
                        <Group.Body>
                            <Group.Section>
                                <Grid
                                    {...grid.bllCnclDtlBllList.grid}
                                    data={bllCnclDtlBllList}
                                    render={render.grid_bllCnclDtlBllList}
                                />
                            </Group.Section>
                        </Group.Body>
                    </Group>
                    * Only Notice for General Notice and Amendment Notice will be cancelled.
                </>
            )}
            {/* 고지취소 등록 */}
            {isVisibleCancellation && (
                <>
                    {/* <Group>
                        <Group.Body>
                            <Group.Title title={"L_ATCH_FILE"} titleSize={2}></Group.Title>
                            <Group.Section>
                                <FormControl {...form.cnclForm.schema.fileSaveDto} />
                            </Group.Section>
                        </Group.Body>
                    </Group> */}
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_CNCL"} titleSize={2}></Group.Title>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.cnclForm.schema.dspoCdSave} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.cnclForm.schema.dspoRmrkSave} />
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                </>
            )}
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0403001q);
                        }}
                    ></Button>
                </Layout.Left>
                {isVisibleSaveBtn && (
                    <Layout.Right>
                        <Button
                            role="save"
                            onClick={() => {
                                handler.saveBllCncl();
                            }}
                        ></Button>
                    </Layout.Right>
                )}
            </Layout>
        </Page>
    );
};
